/**
 * Mobility MVP: Quiz Step Component
 * One question per screen following FIGMA one-task-per-screen pattern
 */

import { useState } from 'react';
import { ChevronLeft, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MobilityInfoSheet } from './MobilityInfoSheet';

export interface MobilityQuestion {
  id: string;
  title: string;
  question: string;
  infoContent: string;
  type: 'yes_no_not_sure' | 'multi_select';
  options?: string[];
}

interface MobilityQuizStepProps {
  question: MobilityQuestion;
  stepNumber: number;
  totalSteps: number;
  selectedAnswer?: string | string[];
  onAnswer: (answer: string | string[]) => void;
  onBack: () => void;
  onNext: () => void;
}

export function MobilityQuizStep({
  question,
  stepNumber,
  totalSteps,
  selectedAnswer,
  onAnswer,
  onBack,
  onNext
}: MobilityQuizStepProps) {
  const [infoSheetOpen, setInfoSheetOpen] = useState(false);

  const handleAnswer = (value: string) => {
    if (question.type === 'multi_select') {
      const current = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      const newAnswer = current.includes(value)
        ? current.filter(a => a !== value)
        : [...current, value];
      onAnswer(newAnswer);
    } else {
      onAnswer(value);
    }
  };

  const canProceed = question.type === 'multi_select' 
    ? Array.isArray(selectedAnswer) && selectedAnswer.length > 0
    : selectedAnswer !== undefined && selectedAnswer !== '';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with back button and progress */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Step {stepNumber} of {totalSteps}
          </span>
        </div>
      </div>

      {/* Main content card */}
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        <Card className="max-w-[420px] mx-auto">
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-xl font-semibold">{question.title}</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setInfoSheetOpen(true)}
                className="h-8 w-8 flex-shrink-0"
              >
                <Info className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-base text-foreground">{question.question}</p>

            {/* Answer options */}
            <div className="space-y-3">
              {question.type === 'yes_no_not_sure' ? (
                <>
                  <Button
                    variant={selectedAnswer === 'yes' ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleAnswer('yes')}
                  >
                    Yes
                  </Button>
                  <Button
                    variant={selectedAnswer === 'no' ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleAnswer('no')}
                  >
                    No
                  </Button>
                  <Button
                    variant={selectedAnswer === 'not_sure' ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleAnswer('not_sure')}
                  >
                    Not sure
                  </Button>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {question.options?.map((option) => {
                    const isSelected = Array.isArray(selectedAnswer) && selectedAnswer.includes(option);
                    return (
                      <Badge
                        key={option}
                        variant={isSelected ? 'default' : 'outline'}
                        className="cursor-pointer px-4 py-2 text-sm"
                        onClick={() => handleAnswer(option)}
                      >
                        {option}
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
        <div className="max-w-[420px] mx-auto">
          <Button
            onClick={onNext}
            disabled={!canProceed}
            className="w-full"
            size="lg"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Info sheet */}
      <MobilityInfoSheet
        isOpen={infoSheetOpen}
        onClose={() => setInfoSheetOpen(false)}
        title="How to determine this?"
        content={question.infoContent}
      />
    </div>
  );
}
